import React from 'react';
import styles from './Devices.module.scss';

interface SkeletonRowProps {
  /** Number of columns the row should span. */
  columns: number;
  /** Stagger index for the shimmer animation delay. */
  index?: number;
}

/**
 * A single skeleton placeholder row that matches the table layout.
 * Each cell renders a rounded bar with a shimmer gradient animation.
 * Rows are staggered so the shimmer cascades down the table.
 */
export default function SkeletonRow({ columns, index = 0 }: SkeletonRowProps) {
  // Vary widths per column so it looks organic, not uniform
  const widths = ['60%', '45%', '70%', '50%', '40%', '55%', '35%'];

  return (
    <tr className={styles.skeletonRow}>
      {Array.from({ length: columns }, (_, col) => (
        <td key={col}>
          <div
            className={styles.skeletonBar}
            style={{
              width: widths[col % widths.length],
              animationDelay: `${index * 80}ms`,
            }}
          />
        </td>
      ))}
    </tr>
  );
}

interface SkeletonRowsProps {
  /** Number of skeleton rows to render. */
  count: number;
  /** Number of columns per row. */
  columns: number;
}

/**
 * Renders `count` skeleton rows. Use in place of real data rows while loading.
 */
export function SkeletonRows({ count, columns }: SkeletonRowsProps) {
  if (count <= 0) return null;

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow key={`skeleton-${i}`} columns={columns} index={i} />
      ))}
    </>
  );
}
